import React, { useState } from 'react';
import { collection, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { useBranches } from '../hooks/useBranches';
import { migrateBranchData } from '../scripts/migrateBranches';

/**
 * BranchManager Component
 * 
 * Admin-only component for managing branch data in Firestore
 * This component should only be accessible to authenticated admins
 */
export const BranchManager: React.FC = () => {
  const { branches, loading, error } = useBranches();
  const [isAdding, setIsAdding] = useState(false);
  const [migrating, setMigrating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contactNumber: '',
    mapUrl: '',
    order: branches.length + 1,
    isActive: true
  });

  const handleMigration = async () => {
    setMigrating(true);
    const result = await migrateBranchData();
    setMigrating(false);
    
    if (result.success) {
      alert('Migration completed successfully! Please refresh the page.');
    } else {
      alert('Migration failed. Check console for errors.');
    }
  };

  const handleAddBranch = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAdding(true);

    try {
      await addDoc(collection(db, 'branches'), {
        ...formData,
        order: Number(formData.order)
      });
      
      // Reset form
      setFormData({
        name: '',
        address: '',
        contactNumber: '',
        mapUrl: '',
        order: branches.length + 2,
        isActive: true
      });
      
      alert('Branch added successfully!');
    } catch (err) {
      console.error('Error adding branch:', err);
      alert('Failed to add branch. Check console for errors.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggleActive = async (branchId: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'branches', branchId), {
        isActive: !currentStatus
      });
      alert('Branch status updated!');
    } catch (err) {
      console.error('Error updating branch:', err);
      alert('Failed to update branch status.');
    }
  };

  const handleDeleteBranch = async (branchId: string, branchName: string) => {
    const confirmed = window.confirm(`Are you sure you want to delete "${branchName}"?`);
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'branches', branchId));
      alert('Branch deleted successfully!');
    } catch (err) {
      console.error('Error deleting branch:', err);
      alert('Failed to delete branch.');
    }
  };

  if (loading) return <div className="p-4">Loading branches...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Branch Manager</h1>

      {/* Migration Section */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <h2 className="text-xl font-semibold mb-2">Initial Data Migration</h2>
        <p className="mb-4 text-sm text-gray-700">
          Run this once to migrate the hardcoded branch data to Firestore. 
          Only run if branches collection is empty.
        </p>
        <button
          onClick={handleMigration}
          disabled={migrating || branches.length > 0}
          className={`px-4 py-2 rounded ${
            migrating || branches.length > 0
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-yellow-500 hover:bg-yellow-600 text-white'
          }`}
        >
          {migrating ? 'Migrating...' : 'Run Migration'}
        </button>
        {branches.length > 0 && (
          <p className="text-sm text-gray-600 mt-2">
            Migration already completed ({branches.length} branches found)
          </p>
        )}
      </div>

      {/* Add Branch Form */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Add New Branch</h2>
        <form onSubmit={handleAddBranch} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Branch Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder="e.g., Main Branch"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Address</label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder="Full address"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Contact Number</label>
            <input
              type="tel"
              value={formData.contactNumber}
              onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder="+63 912 345 6789"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Google Maps URL</label>
            <input
              type="url"
              required
              value={formData.mapUrl}
              onChange={(e) => setFormData({ ...formData, mapUrl: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder="https://maps.app.goo.gl/..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Display Order</label>
            <input
              type="number"
              required
              min="1"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="isActive" className="text-sm font-medium">
              Active (visible on website)
            </label>
          </div>
          
          <button
            type="submit"
            disabled={isAdding}
            className={`px-6 py-2 rounded ${
              isAdding
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            {isAdding ? 'Adding...' : 'Add Branch'}
          </button>
        </form>
      </div>

      {/* Branches List */}
      <div className="bg-white border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Existing Branches</h2>
        <div className="space-y-4">
          {branches.map((branch) => (
            <div
              key={branch.id}
              className="border rounded p-4 flex justify-between items-start"
            >
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{branch.name}</h3>
                <p className="text-gray-600 text-sm">{branch.address}</p>
                {branch.contactNumber && (
                  <p className="text-gray-600 text-sm">📞 {branch.contactNumber}</p>
                )}
                <a
                  href={branch.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 text-sm hover:underline"
                >
                  View on Maps
                </a>
                <p className="text-xs text-gray-500 mt-2">
                  Order: {branch.order} | Status: {branch.isActive ? '✅ Active' : '❌ Inactive'}
                </p>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleActive(branch.id, branch.isActive)}
                  className={`px-3 py-1 rounded text-sm ${
                    branch.isActive
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {branch.isActive ? 'Deactivate' : 'Activate'}
                </button>
                
                <button
                  onClick={() => handleDeleteBranch(branch.id, branch.name)}
                  className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          
          {branches.length === 0 && (
            <p className="text-gray-500 text-center py-8">
              No branches yet. Add one above or run the migration.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BranchManager;
